import call from "./call";
import {
  ComoAgent,
  ComoAgentsResponse,
  ComoAgentCreatePayload,
  ComoToolsResponse,
  ComoConversationsResponse,
  ComoConversationDetail,
  ComoMessagesPage,
  ComoGenerateResponse,
  ComoGenerateRequest,
  ComoConversation,
  ComoEntity,
  McpConnection,
  McpConnectionPayload,
  AgentConversationDocument,
  AgentConversationDocumentsResponse,
  LibraryResponse,
  LibraryQuota,
  LibraryKind,
  TranscriptionResponse,
} from "../Models/comoAgent";

// Admin CRUD
export const getAllComoAgents = async (): Promise<ComoAgent[]> => {
  const response = await call.get<ComoAgentsResponse>("/v1/agent/", {
    snack: {
      error: "comoAgent.fetchError",
    },
  });
  return response.data.agents;
};

export const getSingleComoAgent = async (
  agentId: number,
): Promise<ComoAgent> => {
  const response = await call.get<ComoAgent>(`/v1/agent/${agentId}/`, {
    snack: {
      error: "comoAgent.fetchSingleError",
    },
  });
  return response.data;
};

export const createComoAgent = async (
  data: ComoAgentCreatePayload,
): Promise<ComoAgent> => {
  const response = await call.post<ComoAgent>("/v1/agent/", data, {
    snack: {
      success: "comoAgent.createSuccess",
      error: "comoAgent.createError",
    },
  });
  return response.data;
};

export const updateComoAgent = async (
  agentId: number,
  data: ComoAgentCreatePayload,
): Promise<ComoAgent> => {
  const response = await call.put<ComoAgent>(
    `/v1/agent/${agentId}/`,
    data,
    {
      snack: {
        success: "comoAgent.updateSuccess",
        error: "comoAgent.updateError",
      },
    },
  );
  return response.data;
};

export const deleteComoAgent = async (agentId: number): Promise<void> => {
  await call.delete(`/v1/agent/${agentId}/`, {
    snack: {
      success: "comoAgent.deleteSuccess",
      error: "comoAgent.deleteError",
    },
  });
};

export const getComoTools = async (): Promise<ComoToolsResponse> => {
  const response = await call.get<ComoToolsResponse>("/v1/agent/tools/", {
    snack: {
      error: "comoAgent.fetchToolsError",
    },
  });
  return response.data;
};

// User accessible agents
export const getAccessibleComoAgents = async (): Promise<ComoAgent[]> => {
  const response = await call.get<ComoAgentsResponse>(
    "/v1/agent/accessible/",
    {
      snack: {
        error: "comoAgent.fetchError",
      },
    },
  );
  return response.data.agents;
};

// Chat / Conversations
export const getComoConversations = async (
  page = 1,
): Promise<ComoConversationsResponse> => {
  const response = await call.get<ComoConversationsResponse>(
    "/v1/agent_chat/",
    {
      params: { page },
      snack: {
        error: "comoChat.fetchError",
      },
    },
  );
  return response.data;
};

export const getSingleComoConversation = async (
  conversationId: number,
): Promise<ComoConversationDetail> => {
  const response = await call.get<ComoConversationDetail>(
    `/v1/agent_chat/${conversationId}/`,
    {
      snack: {
        error: "comoChat.fetchSingleError",
      },
    },
  );
  return response.data;
};

export const getConversationMessages = async (
  conversationId: number,
  beforeId?: number,
): Promise<ComoMessagesPage> => {
  const response = await call.get<ComoMessagesPage>(
    `/v1/agent_chat/${conversationId}/messages/`,
    {
      params: beforeId ? { before_id: beforeId } : {},
      snack: {
        error: "comoChat.fetchError",
      },
    },
  );
  return response.data;
};

export const createEmptyComoConversation = async (
  agentId: number,
): Promise<ComoConversation> => {
  const response = await call.post<ComoConversation>(
    "/v1/agent_chat/empty/",
    { agent_id: agentId },
    {
      snack: {
        error: "comoChat.createError",
      },
    },
  );
  return response.data;
};

export const generateComoMessage = async (
  conversationId: number,
  data: ComoGenerateRequest,
): Promise<ComoGenerateResponse> => {
  const response = await call.post<ComoGenerateResponse>(
    `/v1/agent_chat/${conversationId}/generate`,
    data,
    {
      snack: {
        error: "comoChat.generateError",
        409: "comoChat.generateBusy",
      },
    },
  );
  return response.data;
};

export const updateComoConversation = async (
  conversationId: number,
  data: { title?: string; is_pinned?: boolean },
): Promise<ComoConversation> => {
  const response = await call.put<ComoConversation>(
    `/v1/agent_chat/${conversationId}/`,
    data,
    {
      snack: {
        error: "comoChat.updateError",
      },
    },
  );
  return response.data;
};

export const deleteComoConversation = async (
  conversationId: number,
): Promise<void> => {
  await call.delete(`/v1/agent_chat/${conversationId}/`, {
    snack: {
      success: "comoChat.deleteSuccess",
      error: "comoChat.deleteError",
    },
  });
};

export const getComoSteps = async (runId: string): Promise<string[]> => {
  const response = await call.get<{ steps: string[] }>(
    `/v1/agent_chat/steps/`,
    { params: { run_id: runId } },
  );
  return response.data.steps;
};

export const getAccessibleEntities = async (): Promise<ComoEntity[]> => {
  const response = await call.get<{ entities: ComoEntity[] }>(
    "/v1/agent_chat/entities/",
  );
  return response.data.entities;
};

// Voice input
export const transcribeAudio = async (
  audio: Blob,
  filename: string,
  language?: string,
): Promise<string> => {
  const formData = new FormData();
  formData.append("audio", audio, filename);
  if (language) {
    formData.append("language", language);
  }

  const response = await call.post<TranscriptionResponse>(
    "/v1/agent_chat/transcribe/",
    formData,
    { snack: { error: "comoAgent.transcribeError" } },
  );
  return response.data.text;
};

// Conversation documents
export const uploadConversationDocument = async (
  conversationId: number,
  file: File,
): Promise<AgentConversationDocument> => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await call.post<AgentConversationDocument>(
    `/v1/agent_chat/${conversationId}/documents/`,
    formData,
  );
  return response.data;
};

export const getConversationDocuments = async (
  conversationId: number,
): Promise<AgentConversationDocument[]> => {
  const response = await call.get<AgentConversationDocumentsResponse>(
    `/v1/agent_chat/${conversationId}/documents/`,
    {
      snack: {
        error: "comoChat.documentFetchError",
      },
    },
  );
  return response.data.documents;
};

export const deleteConversationDocument = async (
  conversationId: number,
  documentId: number,
): Promise<void> => {
  await call.delete(
    `/v1/agent_chat/${conversationId}/documents/${documentId}/`,
  );
};

// Library (all conversation documents for the current user)
export const getLibraryDocuments = async (params: {
  kind: LibraryKind;
  search: string;
  page: number;
  page_size: number;
}): Promise<LibraryResponse> => {
  const response = await call.get<LibraryResponse>("/v1/agent_chat/library/", {
    params,
    snack: {
      error: "comoChat.libraryFetchError",
    },
  });
  return response.data;
};

export const getLibraryQuota = async (): Promise<LibraryQuota> => {
  const response = await call.get<LibraryQuota>(
    "/v1/agent_chat/library/quota/",
  );
  return response.data;
};

// MCP Connections
export const getAllMcpConnections = async (): Promise<McpConnection[]> => {
  const response = await call.get<{ connections: McpConnection[] }>(
    "/v1/mcp_connection/",
    { snack: { error: "mcpConnection.fetchError" } },
  );
  return response.data.connections;
};

export const getSingleMcpConnection = async (
  id: number,
): Promise<McpConnection> => {
  const response = await call.get<McpConnection>(
    `/v1/mcp_connection/${id}/`,
  );
  return response.data;
};

export const createMcpConnection = async (
  data: McpConnectionPayload,
): Promise<McpConnection> => {
  const response = await call.post<McpConnection>(
    "/v1/mcp_connection/",
    data,
    { snack: { success: "mcpConnection.createSuccess", error: "mcpConnection.createError" } },
  );
  return response.data;
};

export const updateMcpConnection = async (
  id: number,
  data: McpConnectionPayload,
): Promise<McpConnection> => {
  const response = await call.put<McpConnection>(
    `/v1/mcp_connection/${id}/`,
    data,
    { snack: { success: "mcpConnection.updateSuccess", error: "mcpConnection.updateError" } },
  );
  return response.data;
};

export const deleteMcpConnection = async (id: number): Promise<void> => {
  await call.delete(`/v1/mcp_connection/${id}/`, {
    snack: { success: "mcpConnection.deleteSuccess", error: "mcpConnection.deleteError" },
  });
};

export const testMcpConnection = async (
  id: number,
): Promise<{ success: boolean; tools_count?: number; error?: string }> => {
  const response = await call.post(
    `/v1/mcp_connection/${id}/test/`,
    {},
    { snack: { error: "mcpConnection.testError" } },
  );
  return response.data;
};

export const refreshMcpTools = async (
  id: number,
): Promise<McpConnection> => {
  const response = await call.post<McpConnection>(
    `/v1/mcp_connection/${id}/refresh/`,
    {},
  );
  return response.data;
};
